import React, { useMemo } from 'react';
import { ScrollingCarousel } from '@trendyol-js/react-carousel';
import useCollection from 'sdk/hooks/useCollection';
import ViewAll from 'components/shared/ViewAll';
import { getPercent } from 'sdk/utils/assets';
import clsx from 'clsx';
import { round } from 'sdk/utils/formatValues';
import { Link } from 'react-router-dom';
import ContentSection from 'components/shared/ContentSection';
import styles from './TopMovers.module.scss';

function getPercentClassName(asset: any) {
  const latestPrice = asset?.latest_price?.price ?? 0;
  const previousClose = asset?.quote?.previous_close ?? 0;

  if (latestPrice === 0 || previousClose === 0) {
    return 0;
  }

  const percentChange = ((latestPrice - previousClose) / previousClose) * 100;
  return round(percentChange);
}

function TopMovers({
  collectionId, title, viewAllLink, activeCategoryId,
}: {
  collectionId: number,
  title: string,
  viewAllLink?: string,
  activeCategoryId?: number | null,
}) {
  const collectionData = useCollection(collectionId)
  const items = useMemo(() => collectionData?.data?.items?.filter((el) => {
    if (!activeCategoryId) {
      return el;
    }

    return el?.category?.id === activeCategoryId;
  }), [collectionData, activeCategoryId]);

  if (collectionData?.isLoading) {
    return null;
  }

  if (activeCategoryId && !items?.length) {
    return null;
  }

  return (
    <ContentSection
      title={title}
      headerRight={viewAllLink && (<ViewAll url={viewAllLink} />)}
    >
      <ScrollingCarousel className={styles.items}>
        {(items ?? []).sort((a, b) => {
          const percentA = Number(getPercent(a)?.toString().replace(/[+\-\s%]/g, ''));
          const percentB = Number(getPercent(b)?.toString().replace(/[+\-\s%]/g, ''));

          return percentB - percentA;
        }).map((asset: any) => (
          <Link
            key={asset.id}
            className={clsx(
              !asset.active ? styles.item_hidden : styles.item,
              getPercentClassName(asset) > 0 ? styles.itemGreen : styles.itemRed,
            )}
            to={`/asset/${asset.category?.name}/${asset.ticker}`}
          >
            <img src={asset?.icon} alt={asset?.ticker} />
            <div className={styles.row}>
              <span className={styles.ticker}>{asset?.ticker}</span>
              {asset?.latest_price?.price ? (
                <span className={styles.price}>
                  $
                  {asset?.latest_price?.price}
                </span>
              ) : null}
            </div>
            <div className={styles.row}>
              <span className={styles.name}>{asset?.name}</span>
              <span className={clsx(styles.percent, getPercentClassName(asset) > 0 ? styles.percentGreen : styles.percentRed)}>{`${getPercent(asset)}%`}</span>
            </div>
          </Link>
        ))}
      </ScrollingCarousel>
    </ContentSection>
  )
}

export default TopMovers
